
import React, { useState, useEffect } from 'react';
import ThreadedComments from '@/components/social/ThreadedComments';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import { RefreshCcw, Bug, Wrench, AlertTriangle, CheckCircle } from 'lucide-react';
import RealtimeConfigButton from '@/components/social/RealtimeConfigButton';
import VoteDiagnosticTool from '@/components/social/VoteDiagnosticTool';
import RealtimeStatusIndicator from '@/components/social/RealtimeStatusIndicator';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { applyRealtimeConfig } from '@/utils/realtimeConfig';
import { toast } from 'sonner';

const TestCommentsPage: React.FC = () => {
  // This is an example poll ID - replace with a real one from your database
  const pollId = "2f9e6a4e-7b5c-4d8a-9f3b-1c2e4a6d8b9c"; 
  const [channelStatus, setChannelStatus] = useState<{total: number, active: number}>({
    total: 0,
    active: 0
  });
  const [configStatus, setConfigStatus] = useState<{
    checked: boolean;
    pollVotesInRealtime: boolean;
    cardVotesInRealtime: boolean;
  }>({
    checked: false,
    pollVotesInRealtime: false,
    cardVotesInRealtime: false
  });
  const [isChecking, setIsChecking] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  
  // Check publication status
  const checkConfigStatus = async () => {
    setIsChecking(true);
    
    try {
      // Verify configuration
      const result = await applyRealtimeConfig();
      
      if (result.success) {
        const tables = result.message ? [] : []; // This is a simplification since we don't have details anymore
        
        // Assume successful application means tables are included in publication
        setConfigStatus({
          checked: true,
          pollVotesInRealtime: true,
          cardVotesInRealtime: true
        });
      }
    } catch (error) {
      console.error('Error checking config status:', error);
      toast.error('Failed to check realtime configuration');
    } finally {
      setIsChecking(false);
    }
  };

  React.useEffect(() => {
    // Check channel status periodically and track realtime connection
    const checkChannels = () => {
      const channels = supabase.getChannels();
      const activeChannels = channels.filter(c => c.state === 'joined');
      
      setChannelStatus({
        total: channels.length,
        active: activeChannels.length
      });
      
      // Consider connected if we have active channels
      setRealtimeConnected(activeChannels.length > 0);
    };
    
    // Initial check
    checkChannels();
    checkConfigStatus();
    
    // Setup interval
    const intervalId = setInterval(checkChannels, 3000);
    
    return () => clearInterval(intervalId);
  }, []);

  const resetAllChannels = () => {
    const channels = supabase.getChannels();
    console.log(`Resetting ${channels.length} Supabase channels`);
    
    channels.forEach(channel => {
      supabase.removeChannel(channel);
    });
    
    // Force page reload after a short delay to reestablish connections
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };
  
  return (
    <TooltipProvider>
      <div className="container max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Test Comments</h1>
          <RealtimeStatusIndicator isConnected={realtimeConnected} size="md" />
        </div>
        
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Realtime Configuration
                <Badge variant="outline">Debug Tools</Badge>
              </span>
              
              <div>
                {configStatus.checked && (
                  configStatus.pollVotesInRealtime ? 
                    <Badge variant="outline" className="flex gap-1 ml-2 text-green-600 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                      <CheckCircle className="h-3 w-3" />
                      <span className="text-xs">poll_comment_votes</span>
                    </Badge> :
                    <Badge variant="outline" className="flex gap-1 ml-2 text-amber-600 bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
                      <AlertTriangle className="h-3 w-3" />
                      <span className="text-xs">poll_comment_votes</span>
                    </Badge>
                )}
              </div>
            </CardTitle>
            <CardDescription>
              If upvotes and downvotes aren't updating in real-time, try applying
              the realtime configuration to the database.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <RealtimeConfigButton />
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetAllChannels}
                className="flex items-center gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                Reset Channels ({channelStatus.active}/{channelStatus.total})
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={checkConfigStatus}
                disabled={isChecking}
                className="flex items-center gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                Check Config
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                asChild
                className="flex items-center gap-2"
              >
                <Link to="/realtime-debug">
                  <Bug className="h-4 w-4" />
                  Realtime Debug
                </Link>
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <p>Channel status: {channelStatus.active} active out of {channelStatus.total} total</p>
              <p className="mt-2">Applying the config ensures vote tables have proper REPLICA IDENTITY settings 
              and are added to the supabase_realtime publication.</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Add Vote Diagnostics Tool */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Vote Diagnostics</CardTitle>
            <CardDescription>
              Test vote functionality and diagnose realtime updates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VoteDiagnosticTool contentType="poll" />
          </CardContent>
        </Card>
        
        <div className="border rounded-lg p-6">
          <ThreadedComments 
            contentId={pollId} 
            contentType="poll" 
            initialPageSize={5} 
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default TestCommentsPage;
